import Dialog, { DialogProps } from '@/components/Dialog';
import { PATH } from '@/constants/path';
import { useFeedBackOutlet } from '@/modules/Context/FeedbackContextProvider';
import { JobAlertDialogText } from '@/modules/JobAlerts';
import JobAlertChip from '@/modules/JobAlerts/components/JobAlertChip/JobAlertChip';
import { IAlert, IQuery, JobAlertFrequency } from '@/types/alerts';
import { getJobAlertQueryDisplayValues, purgeQuery } from '@/utils/jobListingHelper';
import { useAnalytics } from '@/utils/useAnalytics';
import { saveAlerts, updateAlerts } from '@/utils/useRequest';
import { yupResolver } from '@hookform/resolvers';
import { useRouter } from 'next/router';
import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { Button, FormControlLabel, Radio, RadioGroup, TextField } from '@material-ui/core';

import styles from './JobAlertPreferencesDialog.module.scss';

const JOB_ALERT_REACH_MAXIMUM_NUMBER = 10100;
const JOB_ALERT_LIVE_REACH_MAXIMUM_NUMBER = 10101;

const getDialogTitle = (hasReachedTotalMaximum, hasReachedLiveMaximum, isEditing) => {
  if (hasReachedTotalMaximum) {
    return "Whoa! You've reached the maximum number of job alerts";
  }
  if (hasReachedLiveMaximum) {
    return "Whoa! You've reached the maximum number of live job alerts";
  }
  if (isEditing) {
    return 'Edit job alert preferences';
  } else {
    return 'Set alert preferences';
  }
};

export interface JobAlertPreferencesDialogProps extends DialogProps {
  alert?: IAlert;
  query: IQuery;
  show: boolean;
  hide: () => void;
  successCallBack?: () => void;
}

interface Inputs {
  name: string;
  frequency: JobAlertFrequency;
}

const SigninSchema = yup.object().shape({
  name: yup.string().trim().required(),
  frequency: yup.string().trim().required(),
});

export const JobAlertPreferencesDialog = ({
  alert,
  query,
  show,
  hide,
  successCallBack,
  ...rest
}: JobAlertPreferencesDialogProps) => {
  const track = useAnalytics();

  const isEditing = !!alert;

  const submitButtonText = isEditing ? 'Save preferences' : 'Save job alert';
  const footerText = isEditing ? '' : 'All saved alerts can be managed from the Job alerts menu';

  const showAsSoonAs = !!query.grade && (!!query.speciality || !!query.rootSpeciality);
  const setFeedBack = useFeedBackOutlet();
  const [processing, setProcessing] = useState(false);
  const [hasReachedTotalMaximum, setHasReachedTotalMaximum] = useState(false);
  const [hasReachedLiveMaximum, setHasReachedLiveMaximum] = useState(false);
  const { register, handleSubmit, errors } = useForm<Inputs>({
    resolver: yupResolver(SigninSchema),
    reValidateMode: 'onChange',
    mode: 'onChange',
  });
  const router = useRouter();

  const onSubmit = async data => {
    const newQuery = purgeQuery<IQuery>(query);
    setProcessing(true);
    const eventName = isEditing ? 'Alert Updated' : 'Alert Created';

    track(eventName, {
      ...newQuery,
      ...data,
    });

    const saveAlertFunction = data => (isEditing ? updateAlerts(alert.id, data) : saveAlerts(data));

    // update the query to send specialty instead of rootSpeciality as that is what the api expects
    const saveAlertQueryData = {
      ...newQuery,
      speciality: newQuery.rootSpeciality,
      rootSpeciality: undefined,
    };

    const saveAlertFunctionData = { ...saveAlertQueryData, ...data };

    const result = await saveAlertFunction(saveAlertFunctionData);
    if ('error' in result) {
      if ('errorCode' in result.error) {
        if (result.error.errorCode === JOB_ALERT_REACH_MAXIMUM_NUMBER) {
          setHasReachedTotalMaximum(true);
        } else if (result.error.errorCode === JOB_ALERT_LIVE_REACH_MAXIMUM_NUMBER) {
          setHasReachedLiveMaximum(true);
        } else {
          setFeedBack(result);
        }
      } else {
        setFeedBack(result);
      }
      return setProcessing(false);
    }
    setFeedBack(result);
    setProcessing(false);
    successCallBack?.();
    hide();
  };

  const closeDialog = () => {
    setHasReachedTotalMaximum(false);
    setHasReachedLiveMaximum(false);
    hide();
  };

  const goToJobAlertPage = () => {
    router.push(PATH.JOBS_ALERTS);
    closeDialog();
  };
  return (
    <>
      <Dialog
        title={getDialogTitle(hasReachedTotalMaximum, hasReachedLiveMaximum, isEditing)}
        show={show}
        onClose={closeDialog}
        {...rest}
      >
        {hasReachedTotalMaximum || hasReachedLiveMaximum ? (
          <JobAlertDialogText closeDialog={closeDialog} goToJobAlertPage={goToJobAlertPage} />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <div className={styles.dialogForm}>
              <div className={styles.groupLabel}>Search filters:</div>
              <div className={styles.tagRow} data-testid="alert-dialog-search-filters">
                {getJobAlertQueryDisplayValues(query).map(item => (
                  <JobAlertChip key={item} label={item} />
                ))}
              </div>
            </div>

            <div className={styles.dialogForm}>
              <TextField
                name="name"
                fullWidth
                defaultValue={alert?.name}
                inputRef={register}
                label="Name of job alert"
                error={!!errors.name?.message}
                helperText={errors.name?.message}
                inputProps={{ 'data-testid': 'alert-dialog-name-field' }}
              />
            </div>
            <div className={styles.dialogForm}>
              <div className={styles.groupLabel}>Notification frequency:</div>
              <RadioGroup name="frequency" defaultValue={JobAlertFrequency.OnceADay}>
                <FormControlLabel
                  inputRef={register}
                  value={JobAlertFrequency.OnceADay}
                  control={<Radio />}
                  label="Alert me once a day"
                  data-testid="alert-dialog-once-radio"
                />
                <FormControlLabel
                  inputRef={register}
                  disabled={!showAsSoonAs}
                  value={JobAlertFrequency.Immediately}
                  control={<Radio />}
                  label="Alert as soon as a job matches my search (requires at least a grade and speciality)"
                  data-testid="alert-dialog-immediately-radio"
                />
              </RadioGroup>
            </div>
            <Button
              type="submit"
              disabled={processing}
              fullWidth
              variant="contained"
              size="large"
              color="secondary"
              data-testid="alert-dialog-submit"
            >
              {submitButtonText}
            </Button>
            {footerText && (
              <div className={styles.footer} data-testid="alert-dialog-footer">
                {footerText}
              </div>
            )}
          </form>
        )}
      </Dialog>
    </>
  );
};
